///
/// EarthBrowser
///
/// Created by Matt Giger on 7/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { setProfileOpen } from "@/redux/dialogSlice"
import { ProfileSection, type ProfileTab } from "./ProfileSection"
import { SkeletonCard } from "@/components/misc/SkeletonCard"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useToast } from "@/components/ui/use-toast"

// Map deep-link section params to profile tabs
const sectionToTab: Record<string, ProfileTab> = {
	'account': 'account',
	'delete-account': 'account',
	'linked-accounts': 'security',
	'security': 'security',
	'devices': 'security',
	'passkeys': 'security',
	'subscription': 'subscription',
	'billing': 'subscription',
	'storage': 'storage',
	'datasets': 'storage',
	'privacy': 'privacy',
	'notifications': 'notifications',
}

export function UserProfileDialog() {
	const dispatch = useAppDispatch()
	const profileOpen = useAppSelector(state => state.dialog.profileOpen)
	const userProfile = useAppSelector(state => state.user.userProfile)
	const [searchParams, setSearchParams] = useSearchParams()
	const [initialTab, setInitialTab] = useState<ProfileTab>('account')
	const { toast } = useToast()

	// Handle query params for opening the profile dialog on a specific tab
	useEffect(() => {
		const profileParam = searchParams.get('profile')
		const sectionParam = searchParams.get('section')
		const errorParam = searchParams.get('error')

		if (profileParam === 'open') {
			// Select the requested tab before opening
			if (sectionParam && sectionToTab[sectionParam]) {
				setInitialTab(sectionToTab[sectionParam])
			}

			// Open the profile dialog
			if (!profileOpen) {
				dispatch(setProfileOpen(true))
			}

			// If there's an error, show a toast
			if (errorParam) {
				toast({
					title: "Account Linking Failed",
					description: decodeURIComponent(errorParam),
					variant: "destructive"
				})
			}

			// Clean up URL params through the router so useSearchParams reflects
			// the change (window.history.replaceState would leave profile=open in
			// router state and this effect would re-open the dialog on close)
			const next = new URLSearchParams(searchParams)
			next.delete('profile')
			next.delete('section')
			next.delete('error')
			setSearchParams(next, { replace: true })
		}
	}, [searchParams, setSearchParams, profileOpen, dispatch, toast])

	function handleOpenChange(open: boolean) {
		dispatch(setProfileOpen(open))
		if (!open) {
			setInitialTab('account')
		}
	}

	return (
		<Dialog open={profileOpen} onOpenChange={handleOpenChange}>
		  {/* Same glass recipe as AuthDialog, so sign-in -> profile is one
		      material; glass-surface remaps the theme tokens inside */}
		  <DialogContent className="glass-surface flex flex-col overflow-hidden p-0 h-[min(78vh,720px)] w-[calc(100vw-1rem)] md:w-[760px] max-w-[calc(100vw-1rem)] md:max-w-[760px] rounded-2xl bg-black/40 dark:bg-black/40 backdrop-blur-xl border border-white/20 dark:border-white/20 text-white shadow-2xl">
			<DialogHeader className="sr-only">
				<DialogTitle>Profile Settings</DialogTitle>
				<DialogDescription>
					Manage your profile, subscription, security, and privacy settings
				</DialogDescription>
			</DialogHeader>
			{!userProfile ? (
				<div className="p-6 h-full flex items-center justify-center">
					<SkeletonCard />
				</div>
			) : (
				<ProfileSection key={`${profileOpen}-${initialTab}`} initialTab={initialTab} />
			)}
		  </DialogContent>
		</Dialog>
	)
}
